import type { LineChartTooltipProps } from './LineChartTooltip';
import * as d3array from 'd3-array';
import * as d3axis from 'd3-axis';
import * as d3sel from 'd3-selection';
import * as d3scale from 'd3-scale';
import * as d3shape from 'd3-shape';
import type { ScaleLinear, ScaleTime } from 'd3';

import { DefaultDateFormatter, intl, useTrackElementResize } from '../../lib';
import type { RefObject } from 'react';
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { D3Axis } from './D3Axis';
import type { LanguageProperty } from '../generated/LanguageProperty';
import { InfoModal } from '../basic';
import { HBox } from '../layout';

export const d3 = { ...d3array, ...d3axis, ...d3sel, ...d3scale, ...d3shape };

export const margin = {
  top: 40,
  right: 30,
  bottom: 30,
  left: 40
};

export const initialTooltipState: TooltipState = {
  data: null,
  visible: false,
  position: {
    x: 0,
    y: 0
  }
};

const UNKNOWN_INFORMATION = `<${intl('common.unknown')}>`;

export type TooltipState = Pick<
  LineChartTooltipProps,
  'data' | 'position' | 'visible' | 'constrainX'
>;

export type LineChartProps = {
  header: string;
  headerModalTitle?: LanguageProperty;
  headerModalContent?: LanguageProperty;
  initialWidth?: number;
  initialHeight?: number;
};

export type DataPointX = {
  x: string;
};

export type DataPointY = {
  value: number;
  unit: string;
  optionalText?: string;
};

export type SingleLine = {
  firstLine: DataPointY;
};

type MultiLine = SingleLine & {
  secondLine: DataPointY;
};

export type SingleLineData = DataPointX & {
  y: SingleLine;
};

export type MultiLineData = DataPointX & {
  y: MultiLine;
};

/**
 * Returns a formatted string with the value and its unit.
 * If the value is `null` or `undefined`, it returns (translated) "<Unknown>".
 *
 * @param value
 *          value
 * @param unit
 *          unit of the value
 *
 * @returns formatted string with value and unit
 */
export function getValueWithUnit(value?: number | null, unit?: string): string {
  const val = value ?? UNKNOWN_INFORMATION;
  return [String(val), unit].filter(Boolean).join(' ');
}

/**
 * Checks if the provided `data` is of type `MultiLineData`.
 *
 * @param data
 *          data to check
 *
 * @returns boolean
 */
export function isMultiLineData(
  data: SingleLineData | MultiLineData | null
): data is MultiLineData {
  return !!(data?.y && 'secondLine' in data.y);
}

/**
 * Custom hook to create a scaled x-axis based on the provided `data` and `width`.
 * It uses d3's `scaleUtc` to create a time-based scale.
 * If no valid `x` values within `data` are provided, it defaults to a domain of 0 to 1.
 *
 * @param data
 *          data used for x-axis scale
 * @param width
 *         width of the x-axis
 *
 * @returns scaled x-axis
 */
export function useScaledXAxis(data: SingleLineData[], width: number) {
  return useMemo(() => {
    const extent = d3.extent(data, d => new Date(d.x ?? '').valueOf());

    if (extent[0] == null || extent[1] == null) {
      // no values were given for x-axis, fallback to a domain of 0 to 1
      return d3.scaleUtc([0, 1], [0, width]);
    }

    return d3.scaleUtc(extent, [0, width]);
  }, [data, width]);
}

/**
 * Custom hook to create refs for the line chart.
 */
export function useChartRefs() {
  const legendRef = useRef<SVGGElement>(null);
  const dotsGroupRef = useRef<SVGGElement>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);

  return { legendRef, dotsGroupRef, svgContainerRef };
}

/**
 * Custom hook to track the dimensions of the line chart container.
 * It uses the `useTrackElementResize` hook to detect changes in the container's size.
 *
 * @param svgContainerRef
 *          ref object to the container where the chart is rendered
 * @param initialWidth
 *          initialWidth of the container
 * @param initialHeight
 *          initialHeight of the container
 *
 * @returns the new width and height of the line chart container
 */
export function useTrackChartDimensions(
  svgContainerRef: RefObject<HTMLDivElement>,
  initialWidth: number,
  initialHeight: number
) {
  const [width, setWidth] = useState(initialWidth);
  const [height, setHeight] = useState(initialHeight);

  useTrackElementResize(svgContainerRef, true, () => {
    if (svgContainerRef.current) {
      setWidth(
        svgContainerRef.current.offsetWidth - margin.left - margin.right
      );
      setHeight(
        svgContainerRef.current.offsetHeight - margin.top - margin.bottom
      );
    }
  });

  return { width, height };
}

type ClosestDataPointReturnType = {
  closestPoint: SingleLineData | MultiLineData;
  mousePosition: [number, number];
};

/**
 * Helper method to get the closest data point based on the mouse position.
 * Since the line can't really tell us anything about the exact value I'm hovering over, the value
 * is calculated based on the mouse position and the xScale instead.
 *
 * @param data
 *          chart data to search through
 * @param dotsGroupRef
 *          ref object to the svg, in which the data points are rendered
 * @param x
 *          scaled x-axis
 *
 * @returns the closest data point and the mouse position
 */
export function getClosestPointAndMousePosition(
  data: SingleLineData[] | MultiLineData[],
  dotsGroupRef: RefObject<SVGGElement>,
  x: ScaleTime<number, number>
): ClosestDataPointReturnType | undefined {
  if (!dotsGroupRef.current) {
    return;
  }
  const mousePosition = d3.mouse(dotsGroupRef.current); // mouse position within the svg element as [x, y]
  const dateBasedOnMouse = x.invert(mousePosition[0] - margin.left);

  // find the date closest to the one based on the mouse cursor
  // we can't use an equals check because the scaleUtc doesn't use our data 1:1
  const closestPoint = data.reduce((a, b) =>
    Math.abs(new Date(a.x ?? '').valueOf() - dateBasedOnMouse.valueOf()) <
    Math.abs(new Date(b.x ?? '').valueOf() - dateBasedOnMouse.valueOf())
      ? a
      : b
  );

  return { closestPoint, mousePosition };
}

/**
 * Hook to handle the tooltip state and show/hide the tooltip based on mouse events.
 *
 * @param data
 *          chart data
 * @param dotsGroupRef
 *          ref object to the svg, in which the data points are rendered
 * @param x
 *          scaled x-axis
 * @param y
 *          scaled y-axis
 * @param width
 *          width of the chart
 *
 * @returns an object containing the tooltip state and handlers to show/hide the tooltip
 */
export function useTooltipHandler(
  data: SingleLineData[] | MultiLineData[],
  dotsGroupRef: RefObject<SVGGElement>,
  x: ScaleTime<number, number>,
  y: ScaleLinear<number, number>,
  width: number
) {
  const [tooltipState, setTooltipState] = useState(initialTooltipState);

  const showTooltip = useCallback((): void => {
    const closestPointAndMousePosition = getClosestPointAndMousePosition(
      data,
      dotsGroupRef,
      x
    );

    if (!closestPointAndMousePosition) {
      return;
    }

    const { closestPoint, mousePosition } = closestPointAndMousePosition;
    let valueToDisplay: SingleLineData | MultiLineData;

    if (isMultiLineData(closestPoint)) {
      const firstLineCloser = isFirstLineCloser(
        y,
        closestPoint,
        mousePosition[1]
      );

      valueToDisplay = {
        x: closestPoint.x,
        y: {
          firstLine: {
            ...closestPoint.y.firstLine,
            optionalText: firstLineCloser
              ? closestPoint.y.firstLine.optionalText
              : ''
          },
          secondLine: {
            ...closestPoint.y.secondLine,
            optionalText: firstLineCloser
              ? ''
              : closestPoint.y.secondLine.optionalText
          }
        }
      };
    } else {
      valueToDisplay = {
        x: closestPoint.x,
        y: {
          firstLine: closestPoint.y.firstLine
        }
      };
    }

    setTooltipState({
      visible: true,
      data: valueToDisplay,
      position: {
        x: mousePosition[0],
        y: mousePosition[1]
      },
      constrainX: width + margin.left
    });
  }, [data, dotsGroupRef, x, y, width]);

  // handler for hiding the tooltip
  const hideTooltip = () => {
    setTooltipState(initialTooltipState);
  };

  return { tooltipState, showTooltip, hideTooltip };
}

/**
 * Custom hook to set up the layout effect for showing and hiding a tooltip.
 *
 * @param dotsGroupRef
 *          ref object to the svg, in which the data points are rendered
 * @param showTooltip
 *          handler to show the tooltip
 * @param hideTooltip
 *          handler to hide the tooltip
 */
export function useChartLayoutEffect(
  dotsGroupRef: RefObject<SVGGElement>,
  showTooltip: () => void,
  hideTooltip: () => void
) {
  useLayoutEffect(() => {
    if (dotsGroupRef.current) {
      // unfortunately, we need to use `d3.on` so that `d3.mouse` within the showTooltip works
      d3.select(dotsGroupRef.current)
        .on('mousemove', showTooltip)
        .on('mouseout', hideTooltip);
    }
  }, [dotsGroupRef, hideTooltip, showTooltip]);
}

/**
 * Checks if the first line in a MultiLineData is closer to the mouse position than the second line.
 *
 * @param y
 *          scaled y-axis
 * @param closestPoint
 *          closest point returned from `getClosestPointAndMousePosition`
 * @param mousePositionY
 *          y position of the mouse
 *
 * @returns boolean
 */
function isFirstLineCloser(
  y: ScaleLinear<number, number>,
  closestPoint: MultiLineData,
  mousePositionY: number
): boolean {
  const firstLineYPosition = y(closestPoint.y.firstLine.value);
  const secondLineYPosition = y(closestPoint.y.secondLine.value);

  // get the distances of the lines relative to Y position of the mouse
  const firstLineDistance = Math.abs(
    (firstLineYPosition as number) - mousePositionY
  );
  const secondLineDistance = Math.abs(
    (secondLineYPosition as number) - mousePositionY
  );

  return firstLineDistance < secondLineDistance;
}

type SharedLineChartSetup = {
  data: SingleLineData[] | MultiLineData[];
  x: ScaleTime<number, number>;
  y: ScaleLinear<number, number>;
  height: number;
  width: number;
  firstLineLegend: string;
  legendRef: RefObject<SVGGElement>;
};

/**
 * Shared setup of the line charts, for SingleLineChart and MultiLineChart.
 * This includes things like axes dimensions, labels and legend positioning.
 *
 * @param data
 *          data to be displayed in the chart
 * @param x
 *          scaled x-axis
 * @param y
 *          scaled y-axis
 * @param height
 *          height of the chart
 * @param width
 *          width of the chart
 * @param firstLineLegend
 *          text to be displayed in the legend for the first line
 * @param legendRef
 *          ref object to the legend SVG element
 *
 * @return JSX element containing the shared setup of the line charts
 */
export function SharedLineChartSetup({
  data,
  x,
  y,
  height,
  width,
  firstLineLegend,
  legendRef
}: Readonly<SharedLineChartSetup>): JSX.Element {
  return (
    <>
      <g className="legend" ref={legendRef}>
        <rect width={15} height={4} transform="translate(0, -6)" />
        <text className="legend-text" transform="translate(20, 0)">
          {firstLineLegend}
        </text>
      </g>
      <g transform={`translate(${margin.left},${height - margin.bottom / 2})`}>
        <D3Axis
          // the transform on the g element above already takes care of this, so we're setting 0
          h={0}
          axis={d3
            .axisBottom(x)
            .ticks(Math.floor(width / 120))
            .tickSizeOuter(0)
            .tickFormat(domainValue =>
              DefaultDateFormatter.formatDate(new Date(domainValue.valueOf()))
            )}
          axisType={'x'}
          d3={d3}
        />
        <text className="x-axis-text">{intl('common.date')}</text>
      </g>
      <g transform={`translate(${margin.left},0)`}>
        <D3Axis
          h={height}
          axis={d3.axisLeft(y).ticks(Math.floor(height / 40))}
          axisType={'y'}
          d3={d3}
        />
        <text
          className="y-axis-text"
          transform={`rotate(270) translate(-${
            (height + margin.bottom + margin.top) / 2
          }, -45)`}
        >
          {data[0].y.firstLine.unit}
        </text>
      </g>
    </>
  );
}

/**
 * Header component for the LineCharts.
 *
 * @param header
 *          text to be displayed in the header
 * @param headerModalTitle
 *          title of the modal
 * @param headerModalContent
 *          content of the modal
 *
 * @return JSX element containing the header and modal
 */
export function LineChartHeader({
  header,
  headerModalTitle,
  headerModalContent
}: Readonly<
  Omit<LineChartProps, 'initialHeight' | 'initialWidth'>
>): JSX.Element {
  return (
    <HBox>
      {header && <h4 className="s-font">{header}</h4>}
      {headerModalTitle && headerModalContent && (
        <InfoModal
          settings={{
            modalTitle: headerModalTitle,
            modalContent: headerModalContent,
            ariaLabel: 'common.moreinfo',
            orderedList: false,
            heading: '' as LanguageProperty,
            showCloseButton: false
          }}
        />
      )}
    </HBox>
  );
}

/**
 * Helper function to get the SVG properties for the LineCharts.
 *
 * @param width
 *          width of the chart
 * @param height
 *          height of the chart
 *
 * @return object containing the SVG properties
 */
export function getChartSvgProps(width: number, height: number) {
  return {
    width: width + margin.left + margin.right,
    height: height + margin.bottom + margin.top,
    className: 'line-chart-svg',
    viewBox: `0, 0, ${width + margin.left + margin.right}, ${
      height + margin.bottom + margin.top
    }`
  };
}
